import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SearchInputProps {
  /** The search in effect (the list's), not each keystroke. */
  value: string
  /** Called after a pause in typing, on Enter and on clear, with the trimmed text. */
  onChange: (value: string) => void
  placeholder?: string
  'aria-label'?: string
  /** Wait after the last keystroke, in ms (default 300). */
  delay?: number
  maxLength?: number
  className?: string
  'data-testid'?: string
}

/**
 * The search of a list (F-182): first in the bar, takes what is left of the
 * line. Waits for a pause in typing before it asks; ✕ clears at once.
 */
export function SearchInput({ value, onChange, placeholder, delay = 300, maxLength = 100, className, ...rest }: SearchInputProps) {
  const { t } = useTranslation('filters')
  const [draft, setDraft] = React.useState(value)
  const sent = React.useRef(value)
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange

  /* Cleared or set from outside ("Clear filters", a shared link). */
  React.useEffect(() => {
    if (value !== sent.current) {
      sent.current = value
      setDraft(value)
    }
  }, [value])

  const commit = React.useCallback((text: string) => {
    const next = text.trim()
    if (next === sent.current) return
    sent.current = next
    onChangeRef.current(next)
  }, [])

  React.useEffect(() => {
    if (draft.trim() === sent.current) return
    const timer = window.setTimeout(() => commit(draft), delay)
    return () => window.clearTimeout(timer)
  }, [draft, delay, commit])

  const label = rest['aria-label'] ?? placeholder ?? t('search')
  return (
    <div className={cn('relative min-w-0', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      <input
        type="text"
        role="searchbox"
        inputMode="search"
        enterKeyHint="search"
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(draft)
          if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); commit('') }
        }}
        placeholder={placeholder ?? t('search')}
        aria-label={label}
        /* Typing a search is not an unsaved change. */
        data-guard-ignore
        data-testid={rest['data-testid']}
        className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-200 sm:h-9"
      />
      {draft && (
        <button
          type="button"
          onClick={() => { setDraft(''); commit('') }}
          aria-label={t('clearSearch')}
          className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
